import React, { useState } from 'react';
import { ArrowRight, Check } from 'lucide-react';

const NewsletterSignup = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !subscribed || isSubmitting) return;

    setError('');
    setIsSubmitting(true);
    try {
      const response = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName,
          email,
          subscribed,
        }),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.detail || 'Newsletter signup is temporarily unavailable');
      }

      setSubmitted(true);
    } catch (submissionError) {
      setError(submissionError.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="bg-[#f7f1ff] py-20 text-purple-950 sm:py-24">
      <div className="mx-auto max-w-3xl px-4 text-center sm:px-6">
        <p className="mb-4 text-xs font-semibold uppercase tracking-[0.35em] text-purple-600">Stay in the loop</p>
        <h2 className="font-title text-4xl font-bold text-purple-900 sm:text-5xl">
          Keep moving forward.
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-lg leading-8 text-purple-800/75">
          Get first access to new Experiences, membership openings, practitioner spotlights, and discounts.
        </p>

        {submitted ? (
          <div className="mx-auto mt-8 flex max-w-md items-center justify-center gap-2 rounded-xl border border-green-200 bg-green-50 px-5 py-4 text-sm font-medium text-green-800">
            <Check className="h-5 w-5" />
            Thanks — you&apos;re on the list.
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mx-auto mt-8 max-w-xl">
            <div className="grid gap-3 sm:grid-cols-2">
              <label htmlFor="newsletter-first-name" className="sr-only">First name</label>
              <input
                id="newsletter-first-name"
                type="text"
                required
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
                placeholder="First name"
                autoComplete="given-name"
                className="min-w-0 rounded-lg border border-purple-200 bg-white px-4 py-3 text-purple-950 outline-none ring-purple-300 placeholder:text-purple-400 focus:ring-2"
              />
              <label htmlFor="newsletter-last-name" className="sr-only">Last name</label>
              <input
                id="newsletter-last-name"
                type="text"
                required
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
                placeholder="Last name"
                autoComplete="family-name"
                className="min-w-0 rounded-lg border border-purple-200 bg-white px-4 py-3 text-purple-950 outline-none ring-purple-300 placeholder:text-purple-400 focus:ring-2"
              />
            </div>
            <div className="mt-3 flex flex-col gap-3 sm:flex-row">
              <label htmlFor="newsletter-email" className="sr-only">Email</label>
              <input
                id="newsletter-email"
                type="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="Your email address"
                autoComplete="email"
                className="min-w-0 flex-1 rounded-lg border border-purple-200 bg-white px-4 py-3 text-purple-950 outline-none ring-purple-300 placeholder:text-purple-400 focus:ring-2"
              />
              <button
                type="submit"
                disabled={!subscribed || isSubmitting}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-purple-900 px-6 py-3 text-sm font-semibold uppercase tracking-[0.14em] text-white transition hover:bg-purple-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSubmitting ? 'Submitting...' : 'Submit'} <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </form>
        )}
        <label className="mt-4 flex items-center justify-center gap-2 text-xs text-purple-700/70">
          <input
            type="checkbox"
            checked={subscribed}
            onChange={(event) => setSubscribed(event.target.checked)}
            className="h-3.5 w-3.5 rounded border-purple-300 text-purple-700"
          />
          Yes, subscribe me to your newsletter.
        </label>
        {error && <p className="mt-3 text-sm text-red-700" role="alert">{error}</p>}
      </div>
    </section>
  );
};

export default NewsletterSignup;